import React, { useEffect, useState } from 'react';
import {
  Box,
  styled,
  Grid,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../../../components/PageHeader';

import TableInOut from './TableInOut';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { api } from '../../../api';
import { Company } from '../../../schema/company';
import { ScanType } from '../../../schema/log';
import { PersonType } from '../../../schema/person';
import MultiSelect from '../../../components/MultiSelect';
import { formatDate } from '../../../util';

import { Link } from 'react-router-dom';

const NumberInOut = () => {
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');

  const [companySearchKeyword, setCompanySearchKeyword] = useState<string>('');
  const [filterCompanyIDList, setFilterCompanyIDList] = useState<number[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[]>([]);
  const [filterScanTypes, setFilterScanTypes] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(new Date());
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);

  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [scanTypes, setScanTypes] = useState<ScanType[]>([]);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }

    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', filterID);
    }
    if (filterName) {
      searchParams.set('name', filterName);
    }
    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    if (filterScanTypes) {
      searchParams.set('type_id', filterScanTypes);
    }
    if (filterStartDate) {
      searchParams.set('start_date', `${formatDate(filterStartDate)}`);
    }
    if (filterEndDate) {
      searchParams.set('end_date', `${formatDate(filterEndDate)}`);
    }
    setIsExportLoading(true);
    api
      .get(`/in-out/export`, {
        params: searchParams,
        responseType: 'blob'
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'in-out_export_data.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  useEffect(() => {
    if (companySearchKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', companySearchKeyword);
        searchParams.set('limit', '25');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    } else {
      api
        .get<Company[]>(`/company`, {
          params: {
            limit: 10
          }
        })
        .then((res) => {
          const { data } = res;
          setCompanies(data);
        });
    }
  }, [companySearchKeyword]);

  useEffect(() => {
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
    api.get<ScanType[]>(`/acc_device_scan_type`).then((res) => {
      const { data } = res;
      setScanTypes(data);
    });
  }, []);

  return (
    <Container>
      <PageHeader text="ข้อมูลการแลกบัตรล่าสุด" />
      <Button component={Link} to={`/summary-in-out`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        สรุปจำนวนคนเข้า - ออกพื้นที่
      </Button>
      <Box>
        <Paper sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" display="inline" sx={{ py: 1 }}>
              การแลกบัตรเข้าออก
            </Typography>
            <Button variant="outlined" onClick={handleExportCSVFile} disabled={isExportLoading}>
              Export
            </Button>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} sx={{ padding: 2 }}>
              <Grid item md={3}>
                <TextField
                  id="search-text-label"
                  label="ค้นหาเลขบัตรประชาชน"
                  variant="outlined"
                  placeholder="ค้นหาเลขบัตรประชาชน"
                  onChange={(event) => setFilterID(event.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  autoComplete="off"
                />
              </Grid>
              <Grid item md={3}>
                <TextField
                  id="search-text-label"
                  label="ค้นหาชื่อ - นามสกุล"
                  variant="outlined"
                  placeholder="ค้นหาชื่อ - นามสกุล"
                  onChange={(event) => setFilterName(event.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  autoComplete={'off'}
                />
              </Grid>
              <Grid item md={3}>
                <MultiSelect
                  options={personTypes.map((data) => ({
                    id: data.id.toString(),
                    name: data.type_name
                  }))}
                  onChange={(data) => setFilterPersonTypeIDList(data)}
                  label="ประเภทบุคคล"
                />
              </Grid>
              <Grid item md={3}>
                <Autocomplete
                  multiple
                  limitTags={2}
                  disablePortal
                  options={companies}
                  getOptionLabel={(option) => option.company_name}
                  renderInput={(params) => <TextField {...params} label="บริษัท" fullWidth />}
                  onChange={(_, value) => setFilterCompanyIDList(value.map((data) => data.id))}
                  onInputChange={(_, newInputValue) => {
                    setCompanySearchKeyword(newInputValue);
                  }}
                  fullWidth
                />

                {/* <Autocomplete
                  multiple
                  limitTags={2}
                  disablePortal
                  options={companies}
                  getOptionLabel={(option) => (option?.company_name ? option.company_name : '')}
                  renderInput={(params) => <TextField {...params} label="บริษัท" placeholder="บริษัท" />}
                  onChange={(_, value) => {
                    setCompanySearchKeyword('');
                    setFilterCompanyIDList(value.map((data) => data.id));
                    setFilterCompanyList(value);
                  }}
                  value={filterCompanyList}
                  onInputChange={(_, newInputValue) => {
                    setCompanySearchKeyword(newInputValue);
                  }}
                  isOptionEqualToValue={(option, v) => v?.id == option?.id}
                  fullWidth
          /> */}
              </Grid>
              {/* <Grid item md={3}>
                <Autocomplete
                  multiple
                  limitTags={2}
                  disablePortal
                  options={gates}
                  getOptionLabel={(option) => option.gate_name}
                  renderInput={(params) => <TextField {...params} label="ประตู" fullWidth />}
                  onChange={(_, value) => setFilterGateIDList(value.map((data) => data.id))}
                  fullWidth
                />
              </Grid> */}
              {/* <Grid item md={3}>
                <Autocomplete
                  multiple
                  limitTags={2}
                  disablePortal
                  options={areas}
                  getOptionLabel={(option) => option.area_name}
                  renderInput={(params) => <TextField {...params} label="พื้นที่" fullWidth />}
                  onChange={(_, value) => setFilterAreaIDList(value.map((data) => data.id))}
                  fullWidth
                />
              </Grid> */}
              {/* <Grid item md={3}>
          
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="วันที่เริ่มต้น"
                    inputFormat="dd/MM/yyyy"
                    // value={filterStartDate}
                    // onChange={(value) => setFilterStartDate(value)}
                    // renderInput={(params) => <TextField {...params} fullWidth />}
                    value={filterStartDate}
                    onChange={(value) => setFilterStartDate(value)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item md={3}>
              
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="วันที่สิ้นสุด"
                    inputFormat="dd/MM/yyyy"
                    // value={filterEndDate}
                    // onChange={(value) => setFilterEndDate(value)}
                    // renderInput={(params) => <TextField {...params} fullWidth />}
                    value={filterEndDate}
                    onChange={(value) => setFilterEndDate(value)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid> */}

              <Grid item md={3}>
                <FormControl fullWidth>
                  <InputLabel>{`เข้าออก`}</InputLabel>
                  <Select
                    label={`เข้าออก`}
                    variant="outlined"
                    onChange={(e) => setFilterScanTypes(e.target.value)}
                    value={filterScanTypes}
                    autoComplete="off"
                  >
                    <MenuItem value="">ทั้งหมด</MenuItem>
                    {scanTypes.map((data, index) => (
                      <MenuItem key={index} value={`${data.id}`}>
                        {data.type_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item md={3}>
                <DesktopDatePicker
                  label="วันที่เริ่มต้น"
                  inputFormat="dd/MM/yyyy"
                  value={filterStartDate}
                  onChange={(value) => setFilterStartDate(value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item md={3}>
                <DesktopDatePicker
                  label="วันที่สิ้นสุด"
                  inputFormat="dd/MM/yyyy"
                  value={filterEndDate}
                  onChange={(value) => setFilterEndDate(value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>

        <TableInOut
          filterID={filterID}
          filterName={filterName}
          filterCompanyIDList={filterCompanyIDList}
          filterPersonTypeIDList={filterPersonTypeIDList}
          filterScanTypes={filterScanTypes}
          filterStartDate={filterStartDate}
          filterEndDate={filterEndDate}
        />
      </Box>
    </Container>
  );
};

export default NumberInOut;

const Container = styled('div')`
  padding: 16px;
`;
